import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Wrench, Plus, Loader2, Trash2, Camera, X, AlertTriangle, Link2, ChevronDown, Package,
} from 'lucide-react'
import { apiGet, postJson, patchJson, apiDelete } from '../../lib/api.js'
import { generateThumbnail } from '../../lib/thumbnail.js'
import { uploadFile } from '../../lib/uploadDirect.js'
import { MAINT_STATUS, maintStatusInfo, maintenanceTotals, fmtDateTH, fmtMoney, parseDate, statusInfo } from '../../lib/asset.js'
import ExpensePicker from './ExpensePicker.jsx'
import ImageLightbox from './ImageLightbox.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'

function blobToDataURL(blob) {
  return new Promise((resolve) => {
    const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = () => resolve(''); r.readAsDataURL(blob)
  })
}
async function uploadPhotos(assetId, files) {
  const out = []
  for (const file of files) {
    if (!/^image\//.test(file.type || '')) continue
    // Direct to Wasabi where CORS allows, else the old proxy — uploadDirect.js.
    const obj = await uploadFile('/api/asset-upload', { asset_id: String(assetId), kind: 'maint' }, file)
    const thumb = await generateThumbnail(file).catch(() => null)
    if (thumb) obj.thumbnail = await blobToDataURL(thumb)
    out.push(obj)
  }
  return out
}
function proxied(loc) { return `/api/asset-file/${loc.split('/').map(encodeURIComponent).join('/')}` }

/* A repair record and the asset's own status are two different things, and they
 * drift: a machine sits "ใช้งาน" in the register while it is actually in bits on
 * a bench. This maps a maintenance status to the asset status it implies, so the
 * panel can offer to keep the register honest in one click.
 * cancelled → null: a cancelled request says nothing about the machine. */
const ASSET_STATUS_FOR = {
  requested:   'broken',
  in_progress: 'repair',
  done:        'active',
  cancelled:   null,
}

function StatusPill({ status }) {
  const s = maintStatusInfo(status)
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${s.tone}`}>{s.label}</span>
}

function PhotoRow({ files, onOpen }) {
  if (!Array.isArray(files) || !files.length) return null
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {files.map((f, i) => (
        <button key={i} type="button" onClick={() => onOpen?.(i)} className="block w-14 h-14 rounded-lg overflow-hidden ring-1 ring-slate-200 hover:ring-brand-300">
          <img src={f.thumbnail || proxied(f.location)} alt="" className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  )
}

/* ── create form ───────────────────────────────────────────────────────────── */
function CreateForm({ assetId, onDone, onCancel }) {
  const [issue, setIssue] = useState('')
  const [picked, setPicked] = useState([])
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const fileRef = useRef(null)

  function onPick(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    setPicked((p) => [...p, ...files.map((f) => ({ file: f, preview: URL.createObjectURL(f) }))].slice(0, 4))
  }
  function removePicked(i) {
    setPicked((p) => { const t = p.slice(); const [rm] = t.splice(i, 1); rm?.preview && URL.revokeObjectURL(rm.preview); return t })
  }

  async function save() {
    if (!issue.trim()) { setErr('กรุณาระบุอาการ/ปัญหา'); return }
    setSaving(true); setErr('')
    try {
      const file = picked.length ? await uploadPhotos(assetId, picked.map((p) => p.file)) : []
      await postJson('/api/asset-maintenance', { asset_id: assetId, issue: issue.trim(), status: 'requested', file })
      picked.forEach((p) => p.preview && URL.revokeObjectURL(p.preview))
      onDone?.()
    } catch (e) { setErr(String(e.message || e)) } finally { setSaving(false) }
  }

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50/40 p-3.5 mb-4">
      <div className="flex flex-wrap gap-2 mb-2.5">
        {picked.map((p, i) => (
          <div key={i} className="relative w-14 h-14">
            <img src={p.preview} alt="" className="w-14 h-14 rounded-lg object-cover ring-1 ring-slate-200" />
            <button onClick={() => removePicked(i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-700 text-white flex items-center justify-center hover:bg-rose-600"><X size={12} /></button>
          </div>
        ))}
        {picked.length < 4 && (
          <button onClick={() => fileRef.current?.click()} className="w-14 h-14 rounded-lg border-2 border-dashed border-brand-300 text-brand-500 flex flex-col items-center justify-center hover:bg-brand-50">
            <Camera size={16} /><span className="text-[9px] mt-0.5">รูป</span>
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple onChange={onPick} className="hidden" />
      </div>
      <textarea value={issue} onChange={(e) => setIssue(e.target.value)} rows={2} placeholder="อาการเสีย / งานที่ต้องซ่อม…"
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13.5px] outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 resize-none" />
      {err && <div className="flex items-center gap-1.5 text-[12.5px] text-rose-600 mt-2"><AlertTriangle size={13} /> {err}</div>}
      <div className="flex items-center justify-end gap-2 mt-3">
        <button onClick={onCancel} disabled={saving} className="px-3 py-1.5 rounded-lg text-[13px] font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">ยกเลิก</button>
        <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} แจ้งซ่อม
        </button>
      </div>
    </div>
  )
}

/* ── one record (expandable manage panel) ──────────────────────────────────── */
function Record({ rec, asset, onChanged }) {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState(rec.status || 'requested')
  const [cost, setCost] = useState(rec.cost ? String(rec.cost) : '')
  const [expenseId, setExpenseId] = useState(Number(rec.expense_id) || 0)
  const [expenseDisplay, setExpenseDisplay] = useState(rec.expense_id > 0 ? `ใบซื้อ #${rec.expense_id}` : '')
  const [remark, setRemark] = useState(rec.remark || '')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [err, setErr] = useState('')
  const [lbIdx, setLbIdx] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const assetId = asset.id

  // Suggest the asset status implied by the maintenance status, but only when it
  // would actually change something. Ticked automatically the moment the user
  // MOVES the status (start work → machine is กำลังซ่อม, one click) and left
  // unticked otherwise — opening a record to add a cost must never quietly
  // reclassify the machine.
  const impliedStatus = ASSET_STATUS_FOR[status] || null
  const statusDiffers = !!impliedStatus && impliedStatus !== (asset.status || 'active')
  const [syncAsset, setSyncAsset] = useState(false)
  const [touched, setTouched] = useState(false)
  function pickStatus(v) {
    setStatus(v)
    setTouched(true)
    setSyncAsset(v !== rec.status)
  }

  async function save() {
    setSaving(true); setErr('')
    try {
      /* The Xano PATCH takes its inputs from a `dblink` to the table, so it
       * inherits the table's required-ness — a genuine partial patch is refused
       * ("Missing param: asset_id"). Resend the record's identity + required
       * columns unchanged alongside the edits; the endpoint picks raw-input keys,
       * so the unchanged ones are a no-op write. modified_by/at are stamped by
       * the proxy. */
      const payload = {
        asset_id: rec.asset_id,
        issue: rec.issue,
        request_by: rec.request_by || 0,
        created_by: rec.created_by || 0,
        status, cost: cost === '' ? 0 : Number(cost), expense_id: expenseId, remark: remark.trim(),
      }
      await patchJson(`/api/asset-maintenance/${rec.id}`, payload)
      // Keep the register in step with the bench.
      if (statusDiffers && syncAsset) await patchJson(`/api/assets/${assetId}`, { status: impliedStatus })
      // Reverse-link the expense to this asset (best-effort). A repair expense is
      // a period cost, NOT a capital asset, so we set asset_id only (not asset=true).
      if (expenseId > 0 && expenseId !== Number(rec.expense_id)) {
        patchJson(`/api/expense/${expenseId}`, { asset_id: assetId }).catch(() => {})
      }
      setOpen(false)
      onChanged?.()
    } catch (e) { setErr(String(e.message || e)) } finally { setSaving(false) }
  }
  async function remove() {
    setConfirmDelete(false)
    setDeleting(true); setErr('')
    try { await apiDelete(`/api/asset-maintenance/${rec.id}`); onChanged?.() }
    catch (e) { setErr(String(e.message || e)); setDeleting(false) }
  }

  return (
    <div className="rounded-xl border border-slate-200 p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={rec.status} />
            <span className="text-[12px] text-slate-400">{fmtDateTH(rec.request_at)}</span>
            {rec.cost > 0 && <span className="text-[12.5px] font-medium text-slate-700 tabular-nums">{fmtMoney(rec.cost)} ฿</span>}
            {rec.expense_id > 0 && <span className="inline-flex items-center gap-0.5 text-[11.5px] text-brand-600"><Link2 size={11} /> #{rec.expense_id}</span>}
          </div>
          <p className="text-[13.5px] text-slate-700 mt-1">{rec.issue}</p>
          <PhotoRow files={rec.file} onOpen={setLbIdx} />
        </div>
        <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-800 shrink-0">
          จัดการ <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="mt-3.5 pt-3.5 border-t border-slate-100 space-y-3">
          <div>
            <span className="block text-[12px] font-medium text-slate-500 mb-1.5">สถานะ</span>
            <div className="flex flex-wrap gap-1.5">
              {MAINT_STATUS.map((s) => (
                <button key={s.value} onClick={() => pickStatus(s.value)}
                  className={`px-2.5 py-1 rounded-lg text-[12.5px] font-medium ring-1 transition ${status === s.value ? maintStatusInfo(s.value).tone : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'}`}>
                  {s.label}
                </button>
              ))}
            </div>
            {statusDiffers && (
              <label className={`mt-2 flex items-start gap-2 rounded-lg px-2.5 py-2 cursor-pointer select-none ${touched ? 'bg-brand-50' : 'bg-slate-50'}`}>
                <input
                  type="checkbox" checked={syncAsset} onChange={(e) => setSyncAsset(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-brand-600 focus:ring-2 focus:ring-brand-500/20"
                />
                <span className="text-[12.5px] text-slate-600 leading-snug">
                  <Package size={12} className="inline mr-1 -mt-0.5 text-slate-400" />
                  เปลี่ยนสถานะสินทรัพย์เป็น <b className="text-slate-800">{statusInfo(impliedStatus).label}</b>
                  <span className="text-slate-400"> (ตอนนี้ {statusInfo(asset.status).label})</span>
                </span>
              </label>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[12px] font-medium text-slate-500 mb-1">ค่าซ่อม (บาท)</span>
              <input type="number" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-[14px] outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10" />
            </label>
          </div>
          <div>
            <span className="block text-[12px] font-medium text-slate-500 mb-1">ผูกกับใบค่าใช้จ่าย (ค่าซ่อม)</span>
            <ExpensePicker value={expenseId} display={expenseDisplay}
              onPick={(e) => { setExpenseId(e.id); setExpenseDisplay(`${e.detail || 'ใบซื้อ'} · #${e.id}`) }}
              onClear={() => { setExpenseId(0); setExpenseDisplay('') }}
              placeholder="ค้นหาใบค่าใช้จ่ายของการซ่อม…" />
          </div>
          <label className="block">
            <span className="block text-[12px] font-medium text-slate-500 mb-1">หมายเหตุ</span>
            <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 resize-none" />
          </label>
          {err && <div className="flex items-center gap-1.5 text-[12.5px] text-rose-600"><AlertTriangle size={13} /> {err}</div>}
          <div className="flex items-center justify-between">
            <button onClick={() => setConfirmDelete(true)} disabled={saving || deleting} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
              {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} ลบ
            </button>
            <button onClick={save} disabled={saving || deleting} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : null} บันทึก
            </button>
          </div>
        </div>
      )}

      <ImageLightbox images={rec.file} index={lbIdx} onClose={() => setLbIdx(null)} onIndex={setLbIdx} />

      {confirmDelete && (
        <ConfirmDialog
          title="ลบรายการซ่อมนี้?"
          message="ประวัติการซ่อมและรูปที่แนบไว้จะหายไปถาวร"
          confirmLabel="ลบรายการ" tone="danger"
          onConfirm={remove}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  )
}

export default function AssetMaintenance({ asset, onChanged }) {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)

  function load() {
    setLoading(true)
    apiGet('/api/asset-maintenance', { asset_id: asset.id, per_page: 200 })
      .then((r) => setRecords(Array.isArray(r) ? r : (r?.items || [])))
      .catch(() => setRecords([]))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [asset.id])

  const totals = maintenanceTotals(records)

  /* The Xano endpoint already sorts id desc, so this is a belt-and-braces pass
   * that makes the newest-first order explicit at the point of render and keeps
   * it correct if the endpoint's sort ever changes. Ties (same request_at) fall
   * back to id, which is monotonic on insert. */
  const ordered = useMemo(() => {
    const at = (r) => {
      const d = parseDate(r?.request_at) || parseDate(r?.created_at)
      return d ? d.getTime() : null
    }
    return records.slice().sort((a, b) => {
      const ta = at(a), tb = at(b)
      if (ta !== null && tb !== null && ta !== tb) return tb - ta
      if (ta !== null && tb === null) return -1   // dated records above undated ones
      if (ta === null && tb !== null) return 1
      return Number(b?.id || 0) - Number(a?.id || 0)
    })
  }, [records])

  function afterChange() { load(); onChanged?.() }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center"><Wrench size={16} strokeWidth={2} /></span>
          <h3 className="text-[14.5px] font-semibold text-slate-800">
            ประวัติการซ่อม <span className="text-slate-400 font-normal">({totals.count})</span>
          </h3>
        </div>
        {!creating && (
          <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100">
            <Plus size={14} /> แจ้งซ่อม
          </button>
        )}
      </div>

      {/* totals strip */}
      {totals.count > 0 && (
        <div className="flex flex-wrap gap-4 mb-3.5 text-[13px]">
          <span className="text-slate-500">ค่าซ่อมสะสม <b className="text-slate-800 tabular-nums">{fmtMoney(totals.totalCost)} ฿</b></span>
          {totals.open > 0 && <span className="text-amber-600">ค้างอยู่ {totals.open} รายการ</span>}
        </div>
      )}

      {creating && <CreateForm assetId={asset.id} onDone={() => { setCreating(false); afterChange() }} onCancel={() => setCreating(false)} />}

      {loading ? (
        <div className="text-center py-6 text-slate-400"><Loader2 size={16} className="inline animate-spin mr-1.5" /> กำลังโหลด…</div>
      ) : !records.length && !creating ? (
        <div className="text-center py-6 text-[13px] text-slate-400">ยังไม่มีประวัติการซ่อม</div>
      ) : (
        <div className="space-y-2.5">
          {ordered.map((rec) => <Record key={rec.id} rec={rec} asset={asset} onChanged={afterChange} />)}
        </div>
      )}
    </section>
  )
}
